import * as React from 'react';
import { Check, Circle, Pause, X } from 'lucide-react';
import { cn } from '../lib/cn';
import { colors } from '../tokens/colors';
import type { StatusKey } from '../tokens/colors';

/**
 * WorkflowTimeline — vertical timeline rendering order_status_history.
 *
 * Each event:
 *   - colored dot = status terminal class (success/error/warning/active)
 *   - timestamp + actor name
 *   - optional reason / metadata
 *
 * RTL: rail + dots positioned via inline-start; timestamps via inline-end.
 *      Connector line uses border-inline-start (logical).
 */

export interface TimelineEvent {
  id: string;
  fromStatus: StatusKey | null;
  toStatus: StatusKey;
  changedAt: Date | string;
  changedBy?: { name: string; role?: string };
  reason?: string;
  isCurrent?: boolean;
}

export interface WorkflowTimelineProps {
  events: TimelineEvent[];
  formatDate?: (d: Date | string) => string;
  /** Translation function for status labels */
  getLabel?: (status: StatusKey) => string;
  className?: string;
}

const DEFAULT_LABELS: Record<StatusKey, string> = {
  draft: 'Draft saved',
  submitted: 'Submitted',
  in_review: 'In review',
  assigned_to_provider: 'Assigned to provider',
  in_production: 'In production',
  quality_check: 'Quality check',
  in_delivery: 'In delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  returned: 'Returned',
  on_hold: 'On hold',
};

function getIcon(status: StatusKey, isCurrent: boolean): React.ReactNode {
  if (status === 'cancelled' || status === 'returned') return <X className="h-3 w-3" />;
  if (status === 'on_hold') return <Pause className="h-3 w-3" />;
  if (status === 'delivered') return <Check className="h-3 w-3" />;
  if (isCurrent) return <Circle className="h-2 w-2 fill-current" />;
  return <Check className="h-3 w-3" />;
}

export function WorkflowTimeline({
  events,
  formatDate = (d) => new Date(d).toLocaleString(),
  getLabel = (s) => DEFAULT_LABELS[s],
  className,
}: WorkflowTimelineProps) {
  return (
    <ol className={cn('relative space-y-4', className)}>
      {events.map((event, idx) => {
        const isLast = idx === events.length - 1;
        const statusColor = colors.status[event.toStatus];

        return (
          <li key={event.id} className="relative ps-10">
            {/* Connector line */}
            {!isLast ? (
              <span
                aria-hidden="true"
                className="absolute top-7 bottom-[-1rem] w-px bg-border-subtle"
                style={{ insetInlineStart: '0.875rem' }}
              />
            ) : null}

            {/* Status dot */}
            <span
              aria-hidden="true"
              className={cn(
                'absolute top-1 inline-flex h-7 w-7 items-center justify-center rounded-full',
                event.isCurrent ? 'ring-2 ring-offset-2 ring-offset-surface-elevated' : '',
              )}
              style={{
                insetInlineStart: '0',
                backgroundColor: statusColor.bg,
                color: statusColor.fg,
                ...(event.isCurrent ? { boxShadow: `0 0 0 2px ${statusColor.dot}` } : {}),
              }}
            >
              {getIcon(event.toStatus, event.isCurrent ?? false)}
            </span>

            <div className="flex flex-col gap-1 min-h-[2rem]">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-sm font-medium text-text-primary">
                  {getLabel(event.toStatus)}
                </span>
                <time className="text-xs text-text-muted tabular">
                  {formatDate(event.changedAt)}
                </time>
              </div>
              {event.changedBy ? (
                <span className="text-xs text-text-secondary">
                  by {event.changedBy.name}
                  {event.changedBy.role ? (
                    <span className="text-text-muted"> · {event.changedBy.role}</span>
                  ) : null}
                </span>
              ) : null}
              {event.reason ? (
                <p className="text-sm text-text-secondary mt-1 italic">{event.reason}</p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
